// Admin Dashboard
// Main admin dashboard with tabs for managing beta users

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Icon from '@/components/ui/Icon';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, 
  BarChart3, 
  MessageSquare, 
  Flag,
  ArrowLeft,
  Shield,
  UserCheck,
  TestTube,
  Bug,
  Database
} from 'lucide-react';

// Import admin components
import AdminUsersList from '@/components/admin/AdminUsersList';
import AdminAnalytics from '@/components/admin/AdminAnalytics';
import AdminFeedback from '@/components/admin/AdminFeedback';
import AdminFeatureFlags from '@/components/admin/AdminFeatureFlags';
import AdminBetaManagement from '@/components/admin/AdminBetaManagement';
import AdminCampManagement from '@/components/admin/AdminCampManagement';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';
// Import existing components instead of creating new ones
import AnalyticsTest from '@/components/test/AnalyticsTest';
import DebugTools from '@/pages/DebugTools';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Check if user is admin
  const isAdmin = () => {
    const email = user?.email || '';
    return email === 'admin@campconnect.app' || email.includes('@campconnect.app');
  };

  useEffect(() => {
    // Wait for user data to be loaded
    if (!user) {
      return;
    }
    
    if (!isAdmin()) {
      navigate('/Dashboard');
      return;
    }
    
    setLoading(false);
  }, [navigate, user]);

  // Show loading while user data is being fetched
  if (!user || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <AppLoadingSpinner message="Loading admin dashboard..." />
      </div>
    );
  }

  if (!isAdmin()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/Dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-brand-600" />
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <p className="text-gray-600">Manage beta users, feedback, and feature flags</p>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto">
            <TabsList className="inline-flex w-auto min-w-full">
              <TabsTrigger value="overview" className="flex items-center gap-2 whitespace-nowrap">
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-2 whitespace-nowrap">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Users</span>
              </TabsTrigger>
              <TabsTrigger value="feedback" className="flex items-center gap-2 whitespace-nowrap">
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Feedback</span>
              </TabsTrigger>
              <TabsTrigger value="beta" className="flex items-center gap-2 whitespace-nowrap">
                <UserCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Beta Access</span>
              </TabsTrigger>
              <TabsTrigger value="analytics-test" className="flex items-center gap-2 whitespace-nowrap">
                <TestTube className="w-4 h-4" />
                <span className="hidden sm:inline">Analytics Test</span>
              </TabsTrigger>
              <TabsTrigger value="debug-tools" className="flex items-center gap-2 whitespace-nowrap">
                <Bug className="w-4 h-4" />
                <span className="hidden sm:inline">Debug Tools</span>
              </TabsTrigger>
              <TabsTrigger value="features" className="flex items-center gap-2 whitespace-nowrap">
                <Flag className="w-4 h-4" />
                <span className="hidden sm:inline">Features</span>
              </TabsTrigger>
              <TabsTrigger value="camps" className="flex items-center gap-2 whitespace-nowrap">
                <Database className="w-4 h-4" />
                <span className="hidden sm:inline">Camp Management</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Analytics Overview</CardTitle>
                <CardDescription>
                  Key metrics and insights about your beta users
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminAnalytics />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  View and manage beta users with detailed statistics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminUsersList />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Feedback Management</CardTitle>
                <CardDescription>
                  Review and respond to user feedback
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminFeedback />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Beta Access Tab */}
          <TabsContent value="beta" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Beta Access Management</CardTitle>
                <CardDescription>
                  Manage approved beta users and review access requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminBetaManagement />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Test Tab */}
          <TabsContent value="analytics-test" className="space-y-6">
            <AnalyticsTest />
          </TabsContent>

          {/* Debug Tools Tab */}
          <TabsContent value="debug-tools" className="space-y-6">
            <DebugTools />
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Feature Flags</CardTitle>
                <CardDescription>
                  View current feature flag configuration (environment-based)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminFeatureFlags />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Camp Management Tab */}
          <TabsContent value="camps" className="space-y-6">
            <AdminCampManagement />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
